import {useDeviceDraft} from '../lib/useDeviceDraft';
import {offlineState,offlineWritable} from '../lib/offlineVault';
import { useQueryClient } from '@tanstack/react-query';
// Patient card registration — Phase 1b + 1c of the revised EMR framework.
//
// Flow (card_type is routed via props so this screen serves Personal, Gynae,
// ANC, Emergency, and Corporate with identical shape; Family has its own
// screen because of the family account + beneficiaries structure):
//   1. Receptionist types phone / name / hospital number into the search bar
//      and presses Find.
//   2. GET /reception/patients/search returns hits + has_strong_match.
//      When has_strong_match=true, the UI pushes "possible duplicate — reuse?"
//      above the Create button. Receptionist can either Open an existing hit
//      or press Create new (which flips an override state).
//   3. POST /reception/patients creates the patient (if new), the card
//      (if the patient didn't already have one of this type at this
//      clinic), and a draft Section A-F registration row. Navigates to the
//      patient's registration view.
//
// Corporate adds two extra fields (employer_name + staff_reference) which
// get serialised into card.notes as "Employer: X | Staff Ref: Y" — this
// lets us ship Corporate without a schema migration. Phase 1c.2 may
// promote these to typed columns.

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PatientCardType, ReceptionPatientHit } from '@pamosi/shared';
import { PATIENT_CARD_TYPE_LABELS } from '@pamosi/shared';
import { ApiError, receptionApi } from '../lib/api';

const PHONE_RE = /^\+[1-9]\d{7,14}$/;

interface Props {
    cardType: PatientCardType;
}

export function PersonalCardScreen({ cardType = 'personal' as PatientCardType }: Partial<Props> = {}) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const cardLabel = PATIENT_CARD_TYPE_LABELS[cardType];
    const isCorporate = cardType === 'corporate';

    const [query, setQuery] = useState(new URLSearchParams(window.location.search).get('search') ?? '');
    const [hits, setHits] = useState<ReceptionPatientHit[] | null>(null);
    const [strongMatch, setStrongMatch] = useState(false);
    const [searching, setSearching] = useState(false);
    const [searchError, setSearchError] = useState<string | null>(null);

    // Create form fields. Phone is primary — it's the mobile Health join key.
    const [requestId,setRequestId]=useDeviceDraft('card-key:'+cardType,()=>crypto.randomUUID());
    const [phone, setPhone] = useDeviceDraft('card-phone:'+cardType,'');
    const [fullName, setFullName] = useDeviceDraft('card-name:'+cardType,'');
    const [hospitalNumber, setHospitalNumber] = useState('');
    // Flipped by the UI when the receptionist confirms "create a new patient
    // despite the duplicate warning". Scopes the override so a stale strong
    // match from a previous search doesn't block the next create.
    const [overrideDuplicate, setOverrideDuplicate] = useState(false);
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);
    // Corporate-only fields serialised into card.notes.
    const [employerName, setEmployerName] = useState('');
    const [staffReference, setStaffReference] = useState('');

    const blocksCreate = useMemo(
        () => strongMatch && hits && hits.length > 0 && !overrideDuplicate,
        [strongMatch, hits, overrideDuplicate],
    );

    async function search() {
        const q = query.trim();
        if (q.length < 2) {
            setSearchError('Type at least 2 characters.');
            return;
        }
        setSearchError(null);
        setSearching(true);
        try {
            const res = await receptionApi.search(q);
            setHits(res.hits);
            setStrongMatch(res.has_strong_match);
            // Reset the override every time the receptionist re-searches —
            // a new query shouldn't inherit the previous "I know, create
            // anyway" decision.
            setOverrideDuplicate(false);

            // If the query looks like a phone number, prefill the create form.
            if (PHONE_RE.test(q)) setPhone(q);
            // If it looks like a human name, prefill as a convenience.
            else if (/^[a-zA-Z][a-zA-Z\s'-]+$/.test(q) && !fullName) setFullName(q);
        } catch (err) {
            setSearchError(
                err instanceof ApiError
                    ? err.message || err.code
                    : 'Search failed. Try again.',
            );
            setHits(null);
            setStrongMatch(false);
        } finally {
            setSearching(false);
        }
    }

    async function createPatient() {
        setCreateError(null);
        if (!PHONE_RE.test(phone)&&!(phone===''&&offlineWritable()&&['personal','anc'].includes(cardType)&&!hospitalNumber&&!isCorporate)) {
            setCreateError('Enter a valid phone in E.164 format (e.g. +2348012345678).');
            return;
        }
        if (fullName.trim().length === 0) {
            setCreateError('Enter the patient\'s full name.');
            return;
        }
        if (isCorporate && employerName.trim().length === 0) {
            setCreateError('Enter the employer / corporate name.');
            return;
        }
        setRequestId(requestId);setCreating(true);
        try {
            const notesPieces: string[] = [];
            if (isCorporate && employerName.trim()) notesPieces.push(`Employer: ${employerName.trim()}`);
            if (isCorporate && staffReference.trim()) notesPieces.push(`Staff Ref: ${staffReference.trim()}`);
            const notes = notesPieces.join(' | ');
            const res = await receptionApi.createPatient({
                clientRequestId:requestId,
                phoneE164: phone,
                fullName: fullName.trim(),
                cardType,
                ...(hospitalNumber.trim() ? { hospitalNumber: hospitalNumber.trim() } : {}),
                ...(notes ? { notes } : {}),
            });
            void queryClient.invalidateQueries({ queryKey: ['clinic-patients'] });
            void queryClient.invalidateQueries({ queryKey: ['patient-history', res.patient.id] });
            void queryClient.invalidateQueries({ queryKey: ['anc-registration', res.patient.id] });
            // Land on the newly-created patient's registration page. Phase
            // 1b.2 will replace the alert with the inline Section A-F form.
            setPhone('');setFullName('');setRequestId(crypto.randomUUID());navigate(`/reception/patient/${res.patient.id}`);
        } catch (err) {
            if (err instanceof ApiError) {
                if (err.code === 'hospital_number_taken') {
                    setCreateError('That hospital number is already assigned at this clinic.');
                } else if (err.code === 'insufficient_role') {
                    setCreateError('Your role can\'t create patients. Ask reception or an admin.');
                } else {
                    setCreateError(err.message || err.code);
                }
            } else {
                setCreateError('Create failed. Try again.');
            }
        } finally {
            setCreating(false);
        }
    }

    function openExisting(patientId: string) {
        navigate(`/reception/patient/${patientId}`);
    }

    return (
        <div className="space-y-6">
            <div>
                <button
                    type="button"
                    className="text-slate-500 text-sm hover:text-slate-700"
                    onClick={() => navigate('/reception')}
                >
                    ← Back to Reception
                </button>
                <h1 className="text-slate-900 text-2xl font-semibold mt-2">{cardLabel}</h1>
                <p className="text-slate-500 text-sm mt-1">
                    Find the patient first. Create a new {cardLabel.toLowerCase()} only if no match looks right.
                </p>
            </div>

            {/* ─── Search ──────────────────────────────────────────────── */}
            <div className="pm-card p-4 space-y-3">
                <div className="text-slate-700 text-sm font-semibold">Find existing patient</div>
                <div className="flex gap-2">
                    <input
                        type="search"
                        className="pm-input flex-1"
                        placeholder="Hospital number, phone (+234…), or name"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') void search(); }}
                    />
                    <button
                        type="button"
                        className="pm-btn-outline whitespace-nowrap"
                        disabled={searching}
                        onClick={() => void search()}
                    >
                        {searching ? 'Searching…' : 'Find'}
                    </button>
                </div>
                {searchError && (
                    <div className="text-rose-700 text-xs">{searchError}</div>
                )}

                {hits && hits.length === 0 && !searching && (
                    <div className="text-slate-500 text-sm">
                        No matches at this clinic. Create a new personal card below.
                    </div>
                )}

                {hits && hits.length > 0 && (
                    <div className="space-y-2">
                        <div className="text-slate-500 text-xs uppercase tracking-wide">
                            {hits.length} match{hits.length === 1 ? '' : 'es'}
                        </div>
                        {strongMatch && !overrideDuplicate && (
                            <div className="pm-card p-3 border-amber-300 bg-amber-50">
                                <div className="text-amber-900 text-sm">
                                    <span className="font-semibold">Possible duplicate.</span>{' '}
                                    One of these looks like the same person. Open the existing record instead of creating a new one — or press <em>Create anyway</em> if this really is a different patient.
                                </div>
                            </div>
                        )}
                        <ul className="space-y-2">
                            {hits.map((h) => (
                                <li key={h.patient_id}>
                                    <button
                                        type="button"
                                        onClick={() => openExisting(h.patient_id)}
                                        className="pm-card p-3 w-full text-left hover:border-slate-300 transition"
                                    >
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="min-w-0">
                                                <div className="text-slate-900 font-medium truncate">
                                                    {h.full_name || '(No name)'}
                                                </div>
                                                <div className="text-slate-500 text-xs mt-0.5">
                                                    {h.phone_e164}
                                                    {h.dob ? ` · DOB ${h.dob}` : ''}
                                                    {h.estimated_age_years ? ` · ~${h.estimated_age_years}y` : ''}
                                                    {h.gender ? ` · ${h.gender}` : ''}
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                {h.hospital_numbers.length > 0 && (
                                                    <div className="text-slate-700 text-xs font-mono">
                                                        #{h.hospital_numbers[0]}
                                                    </div>
                                                )}
                                                {h.active_card_types.length > 0 && (
                                                    <div className="text-slate-400 text-xs mt-0.5">
                                                        {h.active_card_types.join(', ')}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            {/* ─── Create ──────────────────────────────────────────────── */}
            <div className="pm-card p-4 space-y-3">
                <div className="flex items-center justify-between">
                    <div className="text-slate-700 text-sm font-semibold">Create new {cardLabel.toLowerCase()}</div>
                    {blocksCreate && (
                        <button
                            type="button"
                            className="text-amber-700 text-xs underline"
                            onClick={() => setOverrideDuplicate(true)}
                        >
                            Create anyway
                        </button>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <label className="block">
                        <span className="text-slate-500 text-xs">Phone (E.164){offlineState()&&['personal','anc'].includes(cardType)?' · optional for provisional card':''}</span>
                        <input
                            type="tel"
                            className="pm-input mt-1"
                            placeholder="+2348012345678"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value.trim())}
                            disabled={creating}
                        />
                    </label>
                    <label className="block">
                        <span className="text-slate-500 text-xs">Full name</span>
                        <input
                            type="text"
                            className="pm-input mt-1"
                            placeholder="Jane Doe"
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            disabled={creating}
                        />
                    </label>
                    <label className="block md:col-span-2">
                        <span className="text-slate-500 text-xs">
                            Hospital number <span className="text-slate-400">(optional, must be unique at this clinic)</span>
                        </span>
                        <input
                            type="text"
                            className="pm-input mt-1 font-mono"
                            placeholder="e.g. PH-00123"
                            value={hospitalNumber}
                            onChange={(e) => setHospitalNumber(e.target.value)}
                            disabled={creating}
                        />
                    </label>
                    {isCorporate && (
                        <>
                            <label className="block">
                                <span className="text-slate-500 text-xs">Employer / corporate</span>
                                <input
                                    type="text"
                                    className="pm-input mt-1"
                                    placeholder="e.g. Acme Holdings"
                                    value={employerName}
                                    onChange={(e) => setEmployerName(e.target.value)}
                                    disabled={creating}
                                />
                            </label>
                            <label className="block">
                                <span className="text-slate-500 text-xs">Staff reference <span className="text-slate-400">(optional)</span></span>
                                <input
                                    type="text"
                                    className="pm-input mt-1 font-mono"
                                    placeholder="e.g. EMP-00123"
                                    value={staffReference}
                                    onChange={(e) => setStaffReference(e.target.value)}
                                    disabled={creating}
                                />
                            </label>
                        </>
                    )}
                </div>

                {createError && (
                    <div className="text-rose-700 text-xs">{createError}</div>
                )}

                <button
                    type="button"
                    className="pm-btn-primary w-full"
                    disabled={creating || Boolean(blocksCreate)}
                    onClick={() => void createPatient()}
                >
                    {creating ? 'Creating…' : `Create ${cardLabel.toLowerCase()}`}
                </button>
                {blocksCreate && (
                    <p className="text-slate-400 text-xs text-center">
                        A possible duplicate is in the list above. Open it, or press <em>Create anyway</em>.
                    </p>
                )}
            </div>
        </div>
    );
}
